/**
 * SimulationControls - Interaktive Benutzeroberfläche für Simulationsparameter-Steuerung
 * 
 * Diese zentrale Komponente stellt eine benutzerfreundliche Oberfläche für die Konfiguration
 * aller Simulationsparameter bereit und ermöglicht die Auswahl verschiedener Berechnungsstrategien
 * für alle Asset-Klassen und wirtschaftlichen Indikatoren.
 * 
 * Hauptfunktionen:
 * - Intuitive Auswahl von Rate-Berechnungsstrategien (Parameter, Historisch, Statistisch)
 * - Dynamische Anzeige historischer Statistiken mit Tooltip-Integration

 * - Responsive Layout mit strukturierter Parametergruppierung
 * - Integration historischer deutscher Finanzmarktdaten (1992-2023)
 * - Echtzeit-Feedback über ausgewählte Strategien und deren Implikationen
 * 
 * Datenquellen:
 * - Deutsche Inflationsstatistiken (Verbraucherpreisindex)
 * - Tagesgeld-Zinssätze (Deutsche Banken)
 * - Staatsanleihen-Renditen (Deutsche Bundesanleihen)
 * - Internationale Aktienindizes (MSCI World, MSCI ACWI)
 * 
 * @author Portfolio-3Topf Team
 * @version 2.0.0
 * @since 1.0.0
 */

import React, { useState } from 'react';
import {
  Box,
  ColumnLayout,
  Header,
  Select,
  SpaceBetween,
  Table,
  TextContent,
} from '@cloudscape-design/components';
import { SteuerParameter } from '@/types';
// Inline label helpers (previously from labelHelpers.ts)
const getCrisisDefinitionLabel = (value: string): string => {
  switch (value) {
    case 'negative-return': return 'Krise bei negativer Jahresrendite';
    case 'kapitalindex-drawdown': return 'Krise bei Kapitalindex-Rückgang - Ende erst bei Erholung';
    default: return 'Krise bei negativer Jahresrendite';
  }
};

const getInflationOptionLabel = (value: string, steuerParameter?: { inflationsrate?: number }): string => {
  switch (value) {
    case 'keine-inflation': return 'Keine Inflation (0%)';
    case 'parameter-inflation': return `Inflation mit Prozentwert aus Eingabeparameter (${steuerParameter?.inflationsrate || 2.0}%)`;
    case 'historische-inflation': return 'Inflation als Zufallswert aus historische Inflation';
    case 'statistisch-inflation': return 'Inflation als Zufallswert basierend auf Mittelwert und Standardabweichung';
    default: return 'Keine Inflation (0%)';
  }
};

const getInvestmentReturnOptionLabel = (value: string, steuerParameter?: { renditeInvestitionstopf?: number }): string => {
  switch (value) {
    case 'keine-rendite': return 'Keine Rendite (0%)';
    case 'konstante-rendite': return `Konstante Rendite aus Parameterwert (${steuerParameter?.renditeInvestitionstopf || 7.0}%)`;
    case 'historische-rendite': return 'Rendite als Zufallswert aus MSCI World historischen Renditen';
    case 'statistisch-rendite': return 'Rendite als Zufallswert basierend auf Mittelwert und Standardabweichung der historischen Renditen des MSCI World';
    case 'historische-garch': return 'Realistische Krisen-Modellierung: Historische Marktdaten + intelligente Volatilitäts-Cluster (inkl. 2008, 2000, COVID)';
    case 'statistisch-garch': return 'Theoretische Modellierung: Statistische Verteilung + zeitvariable Volatilität (glatte Übergänge, kontrollierbar)';
    default: return `Konstante Rendite aus Parameterwert (${steuerParameter?.renditeInvestitionstopf || 7.0}%)`;
  }
};

const getTagesgeldOptionLabel = (value: string, steuerParameter?: { zinssatzVerbrauchstopf?: number }): string => {
  switch (value) {
    case 'keine-rendite': return 'Keine Rendite (0%)';
    case 'parameter-rendite': return `Rendite aus Parameterwert (${steuerParameter?.zinssatzVerbrauchstopf || 1.5}%)`;
    case 'historisch-tagesgeld': return 'Rendite als Zufallswert aus historischen Tagesgeld-Zinssätzen';
    case 'statistisch-tagesgeld': return 'Rendite als Zufallswert basierend auf Mittelwert und Standardabweichung der historischen Tagesgeld-Zinssätze';
    default: return 'Keine Rendite (0%)';
  }
};

const getZinstopfOptionLabel = (value: string, steuerParameter?: { zinssatzZinstopf?: number }): string => {
  switch (value) {
    case 'keine-rendite': return 'Keine Rendite (0%)';
    case 'parameter-rendite': return `Rendite aus Parameterwert (${steuerParameter?.zinssatzZinstopf || 3.0}%)`;
    case 'historisch-zinstopf': return 'Rendite als Zufallswert aus historischen Zinstopf-Performances';
    case 'statistisch-zinstopf': return 'Rendite als Zufallswert basierend auf Mittelwert und Standardabweichung der historischen Zinstopf-Performances';
    default: return 'Keine Rendite (0%)';
  }
};
import { GARCHRateCalculationIntegration } from '@/utils/garchModeling';
import {
  INFLATIONSDATEN,
  TAGESGELD_ZINSSAETZE,
  ZINSTOPF_PERFORMANCE,
} from '@/utils/inflationConstants';
import { INDEX_RETURNS } from '@/utils/indexConstants';

/**
 * Props für die SimulationControls-Komponente
 *
 * Diese Schnittstelle definiert alle erforderlichen Parameter und Callback-Funktionen
 * für die Konfiguration von Simulationsparametern.
 */
interface SimulationControlsProps {
  /** Aktuelle Steuerparameter mit Grundeinstellungen und Zinssätzen */
  steuerParameter: SteuerParameter;

  /** Aktuell gewählte Krisendefinition */
  crisisDefinition: string;

  /** Aktuell gewählte Inflations-Berechnungsstrategie */
  inflationOption: string;

  /** Aktuell gewählte Investitionsrendite-Berechnungsstrategie */
  investmentReturnOption: string;

  /** Aktuell gewählte Tagesgeld-Zinssatz-Berechnungsstrategie */
  tagesgeldOption: string;

  /** Aktuell gewählte Zinstopf-Rendite-Berechnungsstrategie */
  zinstopfOption: string;

  /** Callback für Änderung der Krisendefinition */
  onCrisisDefinitionChange: (value: string) => void;

  /** Callback für Änderung der Inflations-Berechnungsstrategie */
  onInflationOptionChange: (value: string) => void;

  /** Callback für Änderung der Investitionsrendite-Berechnungsstrategie */
  onInvestmentReturnOptionChange: (value: string) => void;

  /** Callback für Änderung der Tagesgeld-Berechnungsstrategie */
  onTagesgeldOptionChange: (value: string) => void;

  /** Callback für Änderung der Zinstopf-Berechnungsstrategie */
  onZinstopfOptionChange: (value: string) => void;
}

/**
 * SimulationControls - Hauptkomponente für Simulationsparameter-Konfiguration
 *
 * Diese Komponente rendert eine strukturierte Benutzeroberfläche für die Auswahl
 * aller Simulationsparameter mit integrierter Dokumentation und historischen Statistiken.
 *
 * @param props - SimulationControlsProps mit aktuellen Parametern und Änderungs-Callbacks
 * @returns JSX.Element - Vollständige Parametersteuerungs-Oberfläche
 *
 * @example
 * ```tsx
 * <SimulationControls
 *   steuerParameter={parameters}
 *   inflationOption="historische-inflation"
 *   investmentReturnOption="statistisch-rendite"
 *   tagesgeldOption="parameter-rendite"
 *   zinstopfOption="historisch-zinstopf"
 *   onInflationOptionChange={handleInflationChange}
 *   onInvestmentReturnOptionChange={handleInvestmentChange}
 *   onTagesgeldOptionChange={handleTagesgeldChange}
 *   onZinstopfOptionChange={handleZinstopfChange}
 * />
 * ```
 *
 * Features:
 * - Gruppierte Parameterauswahl nach Asset-Klassen
 * - Historische Statistiken mit Tooltip-Integration
 * - Responsive Layout mit adaptiver Spaltenaufteilung
 * - Echtzeit-Feedback über Parameterauswahl
 */
type ParameterType =
  | 'crisis'
  | 'inflation'
  | 'verbrauchstopf'
  | 'zinstopf'
  | 'investitionstopf'
  | null;

export const SimulationControls: React.FC<SimulationControlsProps> = ({
  steuerParameter,
  crisisDefinition,
  inflationOption,
  investmentReturnOption,
  tagesgeldOption,
  zinstopfOption,
  onCrisisDefinitionChange,
  onInflationOptionChange,
  onInvestmentReturnOptionChange,
  onTagesgeldOptionChange,
  onZinstopfOptionChange,
}) => {
  const [activeParameter, setActiveParameter] = useState<ParameterType>('inflation');

  return (
    <ColumnLayout columns={2} borders="vertical">
      {/* First Column: All Select Boxes */}
      <Box>
        <SpaceBetween direction="vertical" size="l">
          {/* Inflation */}
          <Box>
            <Header variant="h3">Berücksichtigung von Inflation</Header>
            <Select
              selectedOption={
                inflationOption
                  ? {
                      label: getInflationOptionLabel(inflationOption, steuerParameter),
                      value: inflationOption,
                    }
                  : null
              }
              onChange={({ detail }) =>
                onInflationOptionChange(detail.selectedOption?.value || 'keine-inflation')
              }
              onFocus={() => setActiveParameter('inflation')}
              options={[
                {
                  label: 'Keine Inflation (0%)',
                  value: 'keine-inflation',
                },
                {
                  label: `Inflation mit Prozentwert aus Eingabeparameter (${steuerParameter?.inflationsrate || 2.0}%)`,
                  value: 'parameter-inflation',
                },
                {
                  label: 'Inflation als Zufallswert aus historische Inflation',
                  value: 'historische-inflation',
                },
                {
                  label:
                    'Inflation als Zufallswert basierend auf Mittelwert und Standardabweichung',
                  value: 'statistisch-inflation',
                },
              ]}
              expandToViewport
            />
          </Box>

          {/* Verbrauchstopf Returns */}
          <Box>
            <Header variant="h3">Rendite des Verbrauchstopfs</Header>
            <Select
              selectedOption={
                tagesgeldOption
                  ? {
                      label: getTagesgeldOptionLabel(tagesgeldOption, steuerParameter),
                      value: tagesgeldOption,
                    }
                  : null
              }
              onChange={({ detail }) =>
                onTagesgeldOptionChange(detail.selectedOption?.value || 'parameter-rendite')
              }
              onFocus={() => setActiveParameter('verbrauchstopf')}
              options={[
                {
                  label: 'Keine Rendite (0%)',
                  value: 'keine-rendite',
                },
                {
                  label: `Rendite aus Parameterwert (${steuerParameter?.zinssatzVerbrauchstopf || 1.5}%)`,
                  value: 'parameter-rendite',
                },
                {
                  label: 'Rendite als Zufallswert aus historischen Tagesgeld-Zinssätzen',
                  value: 'historisch-tagesgeld',
                },
                {
                  label:
                    'Rendite als Zufallswert basierend auf Mittelwert und Standardabweichung der historischen Tagesgeld-Zinssätze',
                  value: 'statistisch-tagesgeld',
                },
              ]}
              expandToViewport
            />
          </Box>

          {/* Zinstopf Returns */}
          <Box>
            <Header variant="h3">Rendite des Zinstopfs</Header>
            <Select
              selectedOption={
                zinstopfOption
                  ? {
                      label: getZinstopfOptionLabel(zinstopfOption, steuerParameter),
                      value: zinstopfOption,
                    }
                  : null
              }
              onChange={({ detail }) =>
                onZinstopfOptionChange(detail.selectedOption?.value || 'parameter-rendite')
              }
              onFocus={() => setActiveParameter('zinstopf')}
              options={[
                {
                  label: 'Keine Rendite (0%)',
                  value: 'keine-rendite',
                },
                {
                  label: `Rendite aus Parameterwert (${steuerParameter?.zinssatzZinstopf || 3.0}%)`,
                  value: 'parameter-rendite',
                },
                {
                  label: 'Rendite als Zufallswert aus historischen Zinstopf-Performances',
                  value: 'historisch-zinstopf',
                },
                {
                  label:
                    'Rendite als Zufallswert basierend auf Mittelwert und Standardabweichung der historischen Zinstopf-Performances',
                  value: 'statistisch-zinstopf',
                },
              ]}
              expandToViewport
            />
          </Box>

          {/* Investitionstopf Returns */}
          <Box>
            <Header variant="h3">Rendite des Investitionstopf</Header>
            <Select
              selectedOption={
                investmentReturnOption
                  ? {
                      label: getInvestmentReturnOptionLabel(
                        investmentReturnOption,
                        steuerParameter
                      ),
                      value: investmentReturnOption,
                    }
                  : null
              }
              onChange={({ detail }) =>
                onInvestmentReturnOptionChange(detail.selectedOption?.value || 'konstante-rendite')
              }
              onFocus={() => setActiveParameter('investitionstopf')}
              options={[
                {
                  label: 'Keine Rendite (0%)',
                  value: 'keine-rendite',
                },

                {
                  label: `Konstante Rendite aus Parameterwert (${steuerParameter?.renditeInvestitionstopf || 7.0}%)`,
                  value: 'konstante-rendite',
                },
                {
                  label: 'Rendite als Zufallswert aus MSCI World historischen Renditen',
                  value: 'historische-rendite',
                },
                {
                  label:
                    'Rendite als Zufallswert basierend auf Mittelwert und Standardabweichung der historischen Renditen des MSCI World',
                  value: 'statistisch-rendite',
                },
                {
                  label:
                    'Realistische Krisen-Modellierung: Historische Marktdaten + intelligente Volatilitäts-Cluster (inkl. 2008, 2000, COVID)',
                  value: 'historische-garch',
                },
                {
                  label:
                    'Theoretische Modellierung: Statistische Verteilung + zeitvariable Volatilität (glatte Übergänge, kontrollierbar)',
                  value: 'statistisch-garch',
                },
              ]}
              expandToViewport
            />
          </Box>

          {/* Crisis Definition */}
          <Box>
            <Header variant="h3">Definition einer Krise</Header>
            <Select
              selectedOption={
                crisisDefinition
                  ? {
                      label: getCrisisDefinitionLabel(crisisDefinition),
                      value: crisisDefinition,
                    }
                  : null
              }
              onChange={({ detail }) =>
                onCrisisDefinitionChange(detail.selectedOption?.value || 'negative-return')
              }
              onFocus={() => setActiveParameter('crisis')}
              options={[
                {
                  label: 'Krise bei negativer Jahresrendite',
                  value: 'negative-return',
                },
                {
                  label: 'Krise bei Kapitalindex-Rückgang - Ende erst bei Erholung',
                  value: 'kapitalindex-drawdown',
                },
              ]}
              expandToViewport
            />
          </Box>
        </SpaceBetween>
      </Box>

      {/* Second Column: Parameter Details */}
      <Box>
        {activeParameter && (
          <SpaceBetween direction="vertical" size="m">
            <Header variant="h3">Parameter-Details</Header>
            {renderParameterDetails(activeParameter, steuerParameter, investmentReturnOption)}
          </SpaceBetween>
        )}
        {!activeParameter && (
          <Box>
            <Header variant="h3">Parameter auswählen</Header>
            <TextContent>
              <p>
                Klicken Sie auf eines der Eingabefelder links, um detaillierte Informationen zu den
                verfügbaren Optionen und historischen Statistiken zu sehen.
              </p>
            </TextContent>
          </Box>
        )}
      </Box>
    </ColumnLayout>
  );
};

/**
 * Helper functions to convert data objects to table-friendly format
 */
const convertInflationDataToTable = () => {
  return Object.entries(INFLATIONSDATEN)
    .map(([year, data]) => ({
      year: parseInt(year),
      germany: data.germany,
      usa: data.usa,
    }))
    .sort((a, b) => b.year - a.year); // Sort descending by year
};

const convertTagesgeldDataToTable = () => {
  return Object.entries(TAGESGELD_ZINSSAETZE)
    .map(([year, data]) => ({
      year: parseInt(year),
      germany: data.germany,
    }))
    .filter(item => item.germany !== null)
    .sort((a, b) => b.year - a.year);
};

const convertZinstopfDataToTable = () => {
  return Object.entries(ZINSTOPF_PERFORMANCE)
    .map(([year, data]) => ({
      year: parseInt(year),
      germany: typeof data === 'number' ? data : (data as { germany: number }).germany,
    }))
    .filter(item => item.germany !== null && item.germany !== undefined)
    .sort((a, b) => b.year - a.year);
};

const convertIndexDataToTable = () => {
  return Object.entries(INDEX_RETURNS)
    .map(([year, data]) => ({
      year: parseInt(year),
      world: data.world,
      acwi: data.acwi,
    }))
    .sort((a, b) => b.year - a.year);
};

/**
 * Renders detailed parameter information for the selected parameter type
 */
const renderParameterDetails = (
  parameterType: ParameterType,
  steuerParameter?: SteuerParameter,
  investmentReturnOption?: string
) => {
  switch (parameterType) {
    case 'crisis':
      return (
        <SpaceBetween direction="vertical" size="m">
          <Box>
            <Box variant="awsui-key-label">Krisendefinition für Simulationen</Box>
            <TextContent>
              <p>
                <strong>Krisen-Szenarien</strong> definieren kritische Marktbedingungen, die das
                Portfolio-Verhalten und Risikomanagement beeinflussen.
              </p>
            </TextContent>
          </Box>

          <Box>
            <Box variant="awsui-key-label">Verfügbare Krisendefinitionen</Box>
            <TextContent>
              <ul>
                <li>
                  <strong>Krise bei negativer Jahresrendite:</strong> Eine Krise wird erkannt, wenn
                  die Jahresrendite negativ ist - einfachste Definition für Crash-Szenarien.
                </li>
                <li>
                  <strong>Krise bei Kapitalindex-Rückgang - Ende erst bei Erholung:</strong> Eine
                  Krise wird durch signifikanten Kapitalverlust (Drawdown) ausgelöst und dauert an,
                  bis sich das Portfolio vollständig erholt hat - realistisch für langfristige
                  Marktrisiken.
                </li>
              </ul>
            </TextContent>
          </Box>

          <Box>
            <Box variant="awsui-key-label">Auswirkungen auf die Simulation</Box>
            <TextContent>
              <p>
                Die Krisenparameter definieren, wann eine Krise vorliegt und wie in einer Krise
                reagiert wird – also ob Entnahmen aus dem Investitionstopf gestoppt und stattdessen
                der Zinstopf genutzt wird.
              </p>
              <ul></ul>
            </TextContent>
          </Box>
        </SpaceBetween>
      );

    case 'inflation':
      return (
        <SpaceBetween direction="vertical" size="m">
          <Box>
            <Box variant="awsui-key-label">Inflationsmodellierung</Box>
            <TextContent>
              <p>
                <strong>Inflation beeinflusst die Kaufkraft</strong> und ist ein entscheidender
                Faktor für langfristige Finanzplanung.
              </p>
            </TextContent>
          </Box>

          <Box>
            <Box variant="awsui-key-label">Verfügbare Optionen</Box>
            <TextContent>
              <ul>
                <li>
                  <strong>Keine Inflation (0%):</strong> Vernachlässigt Kaufkraftverlust -
                  unrealistisch für längere Zeiträume.
                </li>
                <li>
                  <strong>Parameter-Inflation:</strong> Nutzt den eingegebenen festen Inflationswert
                  ({steuerParameter?.inflationsrate || 2.0}%) - einfach aber konstant.
                </li>
                <li>
                  <strong>Historische Inflation:</strong> Bootstrap-Sampling aus realen deutschen
                  Inflationsdaten (1951-2024) - realistische Schwankungen zwischen Deflation und
                  hoher Inflation.
                </li>
                <li>
                  <strong>Statistische Inflation:</strong> Normalverteilte Zufallswerte basierend
                  auf historischem Mittelwert und Standardabweichung - modelliert langfristige
                  Inflationstrends.
                </li>
              </ul>
            </TextContent>
          </Box>

          <Box>
            <Box variant="awsui-key-label">
              🔐 Crypto-Basierte Randomisierung für Statistische Inflation
            </Box>
            <TextContent>
              <p>
                <strong>Jede Simulation nutzt frische crypto.getRandomValues() Seeds</strong> mit
                Inflation-spezifischem Offset (5000) für maximale Szenario-Vielfalt. Kombiniert
                deterministische Jahr-basierte Konsistenz mit hochwertiger Zufälligkeit pro
                Simulationslauf.
              </p>
            </TextContent>
          </Box>

          <Box>
            <Box variant="awsui-key-label">Historische Statistiken</Box>
            <ColumnLayout columns={2} borders="vertical">
              <Box>
                <Box variant="awsui-key-label">Deutschland (1951-2024)</Box>
                <TextContent>
                  <ul>
                    <li>Durchschnittliche Inflation: ~3.5% p.a.</li>
                    <li>Standardabweichung: ~2.7%</li>
                    <li>Minimum: -1.7% (Deflation 1953)</li>
                    <li>Maximum: 10.4% (Ölkrise 1974)</li>
                    <li>Median: 2.7%</li>
                  </ul>
                </TextContent>
              </Box>
              <Box>
                <Box variant="awsui-key-label">USA (1914-2024)</Box>
                <TextContent>
                  <ul>
                    <li>Durchschnittliche Inflation: ~3.3% p.a.</li>
                    <li>Standardabweichung: ~4.7%</li>
                    <li>Minimum: -10.8% (Depression 1921)</li>
                    <li>Maximum: 20.4% (Post-WWI 1918)</li>
                    <li>Median: 2.7%</li>
                  </ul>
                </TextContent>
              </Box>
            </ColumnLayout>
          </Box>

          <Box>
            <Box variant="awsui-key-label">Historische Inflationsdaten</Box>
            <Table
              items={convertInflationDataToTable()}
              columnDefinitions={[
                {
                  id: 'year',
                  header: 'Jahr',
                  cell: item => item.year,
                },
                {
                  id: 'germany',
                  header: 'Deutschland (%)',
                  cell: item => item.germany?.toFixed(1) || '-',
                },
                {
                  id: 'usa',
                  header: 'USA (%)',
                  cell: item => item.usa?.toFixed(1) || '-',
                },
              ]}
              variant="embedded"
              empty="Keine Daten verfügbar"
              trackBy="year"
              ariaLabels={{
                itemSelectionLabel: () => '',
                allItemsSelectionLabel: () => '',
                selectionGroupLabel: '',
              }}
            />
          </Box>
        </SpaceBetween>
      );

    case 'verbrauchstopf':
      return (
        <SpaceBetween direction="vertical" size="m">
          <Box>
            <Box variant="awsui-key-label">Verbrauchstopf (Tagesgeld) Renditemodellierung</Box>
            <TextContent>
              <p>
                <strong>Der Verbrauchstopf</strong> dient der kurzfristigen Liquidität und sollte
                daher konservativ mit niedrigen, aber stabilen Zinssätzen kalkuliert werden.
              </p>
            </TextContent>
          </Box>

          <Box>
            <Box variant="awsui-key-label">Verfügbare Optionen</Box>
            <TextContent>
              <ul>
                <li>
                  <strong>Keine Rendite (0%):</strong> Unverzinstes Guthaben.
                </li>
                <li>
                  <strong>Parameter-Rendite:</strong> Nutzt den eingegebenen festen Zinssatz (
                  {steuerParameter?.zinssatzVerbrauchstopf || 1.5}%) - typisch 1-3% für sichere
                  Anlagen.
                </li>
                <li>
                  <strong>Historische Tagesgeld-Zinssätze:</strong> Bootstrap-Sampling aus realen
                  deutschen Tagesgeld-Zinssätzen (2008-2025) - reflektiert EZB-Zinspolitik und
                  Marktzyklen.
                </li>
                <li>
                  <strong>Statistische Tagesgeld-Zinssätze:</strong> Normalverteilte Zinssätze
                  basierend auf historischem Mittelwert und Standardabweichung, nie negativ (bei
                  0% gekappt, da Banken auf Tagesgeld praktisch keine negativen Zinsen erheben).
                </li>
              </ul>
            </TextContent>
          </Box>

          <Box>
            <Box variant="awsui-key-label">
              🔐 Crypto-Basierte Randomisierung für Statistische Tagesgeld-Zinssätze
            </Box>
            <TextContent>
              <p>
                <strong>Jede Simulation nutzt frische crypto.getRandomValues() Seeds</strong> mit
                Tagesgeld-spezifischem Offset (6000) für maximale Szenario-Vielfalt. Kombiniert
                deterministische Jahr-basierte Konsistenz mit hochwertiger Zufälligkeit pro
                Simulationslauf.
              </p>
            </TextContent>
          </Box>

          <Box>
            <Box variant="awsui-key-label">
              Historische Statistiken (Deutsche Tagesgeld-Zinssätze 2008-2025)
            </Box>
            <TextContent>
              <ul>
                <li>Durchschnittszins: ~2.1% p.a.</li>
                <li>Standardabweichung: ~2.2%</li>
                <li>Minimum: 0.0% (Nullzins-Phase 2014-2022)</li>
                <li>Maximum: 8.1% (Hochzins-Phase 1992)</li>
                <li>Median: 1.2%</li>
              </ul>
            </TextContent>
          </Box>

          <Box>
            <Box variant="awsui-key-label">Historische Tagesgeld-Zinssätze</Box>
            <Table
              items={convertTagesgeldDataToTable()}
              columnDefinitions={[
                {
                  id: 'year',
                  header: 'Jahr',
                  cell: item => item.year,
                },
                {
                  id: 'germany',
                  header: 'Deutschland (%)',
                  cell: item => item.germany?.toFixed(2) || '-',
                },
              ]}
              variant="embedded"
              empty="Keine Daten verfügbar"
              trackBy="year"
              ariaLabels={{
                itemSelectionLabel: () => '',
                allItemsSelectionLabel: () => '',
                selectionGroupLabel: '',
              }}
            />
          </Box>
        </SpaceBetween>
      );

    case 'zinstopf':
      return (
        <SpaceBetween direction="vertical" size="m">
          <Box>
            <Box variant="awsui-key-label">Zinstopf Renditemodellierung</Box>
            <TextContent>
              <p>
                <strong>Der Zinstopf</strong> fungiert als Puffer zwischen Verbrauchstopf und
                Investitionstopf und sollte daher mit mittleren Risiko-Rendite-Charakteristika
                modelliert werden.
              </p>
            </TextContent>
          </Box>

          <Box>
            <Box variant="awsui-key-label">Verfügbare Optionen</Box>
            <TextContent>
              <ul>
                <li>
                  <strong>Keine Rendite (0%):</strong> Unverzinstes Sparkonto.
                </li>
                <li>
                  <strong>Parameter-Rendite:</strong> Nutzt den eingegebenen festen Zinssatz (
                  {steuerParameter?.zinssatzZinstopf || 3.0}%) - typisch 2-4% für Staatsanleihen
                  oder Festgeld.
                </li>
                <li>
                  <strong>Historische Zinstopf-Performances:</strong> Bootstrap-Sampling aus realen
                  deutschen Staatsanleihen-Renditen (1967-2017) - reflektiert Zinspolitik und
                  Marktbedingungen.
                </li>
                <li>
                  <strong>Statistische Zinstopf-Performances:</strong> Normalverteilte Renditen
                  basierend auf historischem Mittelwert und Standardabweichung von Staatsanleihen.
                </li>
              </ul>
            </TextContent>
          </Box>

          <Box>
            <Box variant="awsui-key-label">
              🔐 Crypto-Basierte Randomisierung für Statistische Zinstopf-Performances
            </Box>
            <TextContent>
              <p>
                <strong>Jede Simulation nutzt frische crypto.getRandomValues() Seeds</strong> mit
                Zinstopf-spezifischem Offset (9000) für maximale Szenario-Vielfalt. Kombiniert
                deterministische Jahr-basierte Konsistenz mit hochwertiger Zufälligkeit pro
                Simulationslauf.
              </p>
            </TextContent>
          </Box>

          <Box>
            <Box variant="awsui-key-label">
              Historische Statistiken (Deutsche Staatsanleihen 1967-2017)
            </Box>
            <TextContent>
              <ul>
                <li>Durchschnittsrendite: ~3.4% p.a.</li>
                <li>Standardabweichung: ~2.8%</li>
                <li>Minimum: -0.8% (Negative Zinsen 2019)</li>
                <li>Maximum: 9.4% (Hochzins-Phase 1992)</li>
                <li>Median: 2.8%</li>
              </ul>
            </TextContent>
          </Box>

          <Box>
            <Box variant="awsui-key-label">Historische Staatsanleihen-Renditen</Box>
            <Table
              items={convertZinstopfDataToTable()}
              columnDefinitions={[
                {
                  id: 'year',
                  header: 'Jahr',
                  cell: item => item.year,
                },
                {
                  id: 'germany',
                  header: 'Deutschland (%)',
                  cell: item => item.germany?.toFixed(2) || '-',
                },
              ]}
              variant="embedded"
              empty="Keine Daten verfügbar"
              trackBy="year"
              ariaLabels={{
                itemSelectionLabel: () => '',
                allItemsSelectionLabel: () => '',
                selectionGroupLabel: '',
              }}
            />
          </Box>
        </SpaceBetween>
      );

    case 'investitionstopf':
      return (
        <SpaceBetween direction="vertical" size="m">
          <Box>
            <Box variant="awsui-key-label">Investitionstopf Renditemodellierung</Box>
            <TextContent>
              <p>
                <strong>Der Investitionstopf</strong> ist für langfristiges Wachstum konzipiert und
                bietet die komplexesten Modellierungsoptionen einschließlich
                GARCH-Volatilitätsmodelle.
              </p>
            </TextContent>
          </Box>

          <Box>
            <Box variant="awsui-key-label">Verfügbare Optionen</Box>
            <TextContent>
              <ul>
                <li>
                  <strong>Keine Rendite (0%):</strong> Reine Kapitalerhaltung ohne Wachstum.
                </li>
                <li>
                  <strong>Parameter-Rendite:</strong> Normalverteilte Renditen um den eingegebenen
                  Mittelwert ({steuerParameter?.renditeInvestitionstopf || 7.0}%) mit ~16%
                  Volatilität - Standard-Aktienmarkt-Simulation.
                </li>
                <li>
                  <strong>Konstante Rendite:</strong> Exakt der eingegebene Prozentsatz jedes Jahr -
                  unrealistisch aber für Vergleiche nützlich.
                </li>
                <li>
                  <strong>Historische Rendite:</strong> Bootstrap-Sampling aus echten MSCI World
                  Jahresrenditen (1970-2024) - inkludiert reale Krisen und Boomphasen.
                </li>
                <li>
                  <strong>Statistische Rendite:</strong> Normalverteilung basierend auf MSCI World
                  Statistiken - modelliert langfristige Marktcharakteristika.
                </li>
                <li>
                  <strong>Historische GARCH:</strong> Nutzt echte Marktkrisen (2008, 2000, COVID) 
                  mit intelligenter Volatilitäts-Modellierung - ideal für realistische Stresstests.
                </li>
                <li>
                  <strong>Statistische GARCH:</strong> Theoretisch fundierte Volatilitäts-Dynamik 
                  ohne historische Anomalien - ideal für kontrollierte Langzeit-Projektionen.
                </li>
              </ul>
            </TextContent>
          </Box>

          <Box>
            <Box variant="awsui-key-label">
              🔐 Crypto-Basierte Randomisierung (Technische Innovation)
            </Box>
            <TextContent>
              <p>
                <strong>Jede neue Simulation verwendet frische, hochwertige Zufallszahlen</strong>{' '}
                für maximale Szenario-Vielfalt:
              </p>
              <ul>
                <li>
                  <strong>Crypto Master Seeds:</strong> Jede Simulation startet mit neuen{' '}
                  <code>crypto.getRandomValues()</code> Seeds - browserbasierte
                  Kryptografie-Qualität für echte Zufälligkeit
                </li>
                <li>
                  <strong>Parameter-spezifische Offsets:</strong> Investment-Renditen (Offset 8000),
                  Inflation (5000), Tagesgeld (6000), Zinstopf (9000) - verhindert Korrelationen
                  zwischen Asset-Klassen
                </li>
                <li>
                  <strong>Jahr-basierte Determinismus:</strong> Innerhalb einer Simulation bleiben
                  die Werte pro Jahr konsistent für faire Vergleiche
                </li>
                <li>
                  <strong>Realistische Märkte:</strong> Investment-Renditen können negativ werden
                  (-40% Crashes möglich), andere Raten bleiben nicht-negativ
                </li>
                <li>
                  <strong>Monte Carlo Diversität:</strong> Jede der 1000+ Iterationen erlebt
                  unterschiedliche, aber realistische Marktszenarien
                </li>
              </ul>
              <p style={{ fontStyle: 'italic', color: '#0972d3', marginTop: '10px' }}>
                <strong>Warum wichtig?</strong> Veraltete Pseudo-Zufallsgeneratoren können in lange
                Simulationen Muster zeigen. Crypto-Seeds garantieren, dass jeder Simulationslauf
                völlig unterschiedliche historische Szenarien erlebt - von Deflations-Perioden bis
                zu Hyperinflation, von Boom-Jahren bis zu Finanzkrisen.
              </p>
            </TextContent>
          </Box>

          <Box>
            <Box variant="awsui-key-label">Historische Statistiken (MSCI World 1970-2024)</Box>
            <TextContent>
              <ul>
                <li>Durchschnittsrendite: ~9.8% p.a.</li>
                <li>Standardabweichung: ~16.2%</li>
                <li>Minimum: -43.1% (Finanzkrise 2008)</li>
                <li>Maximum: +61.2% (Erholung 2009)</li>
                <li>Median: 11.4%</li>
                <li>Positive Jahre: ~73%</li>
                <li>Sharpe Ratio: ~0.47</li>
              </ul>
            </TextContent>
          </Box>

          <Box>
            <Box variant="awsui-key-label">Historische Index-Renditen</Box>
            <Table
              items={convertIndexDataToTable()}
              columnDefinitions={[
                {
                  id: 'year',
                  header: 'Jahr',
                  cell: item => item.year,
                },
                {
                  id: 'world',
                  header: 'MSCI World (%)',
                  cell: item => item.world?.toFixed(2) || '-',
                },
                {
                  id: 'acwi',
                  header: 'MSCI ACWI (%)',
                  cell: item => item.acwi?.toFixed(2) || '-',
                },
              ]}
              variant="embedded"
              empty="Keine Daten verfügbar"
              trackBy="year"
              ariaLabels={{
                itemSelectionLabel: () => '',
                allItemsSelectionLabel: () => '',
                selectionGroupLabel: '',
              }}
            />
          </Box>

          {/* GARCH-specific information */}
          {(investmentReturnOption === 'historische-garch' ||
            investmentReturnOption === 'statistisch-garch') && (
            <Box>
              <Box variant="awsui-key-label">GARCH-Modell Parameter</Box>
              <Box>
                {(() => {
                  try {
                    const garchAnalysis =
                      GARCHRateCalculationIntegration.analyzeIndexDataForGARCH('world');
                    return (
                      <TextContent>
                        <p>
                          <strong>Index:</strong> MSCI World ({garchAnalysis.dataPoints}{' '}
                          Datenpunkte)
                        </p>
                        <p>
                          <strong>Historische Kennzahlen:</strong>
                        </p>
                        <ul>
                          <li>Durchschnittsrendite: {garchAnalysis.historicalMean}% p.a.</li>
                          <li>Historische Volatilität: {garchAnalysis.historicalVolatility}%</li>
                          <li>Schiefe: {garchAnalysis.skewness} (Asymmetrie der Verteilung)</li>
                          <li>Kurtosis: {garchAnalysis.kurtosis} (Fat Tails)</li>
                        </ul>
                        <p>
                          <strong>GARCH-Parameter (EGARCH 1,1):</strong>
                        </p>
                        <ul>
                          <li>
                            Volatilitäts-Persistenz (β):{' '}
                            {garchAnalysis.garchParameters.volatilityPersistence}
                          </li>
                          <li>ARCH-Effekt (α): {garchAnalysis.garchParameters.archEffect}</li>
                          <li>
                            Clustering-Effekt (γ):{' '}
                            {garchAnalysis.garchParameters.clusteringEffect || 'N/A'}
                          </li>
                          <li>
                            Leverage-Effekt (θ): {garchAnalysis.garchParameters.leverageEffect}
                          </li>
                        </ul>
                        <p style={{ fontStyle: 'italic', marginTop: '10px' }}>
                          <strong>Volatility Clustering:</strong>{' '}
                          {investmentReturnOption === 'historische-garch'
                            ? 'Kombiniert historische Renditen mit realistischer Krisen-Struktur'
                            : 'Erzeugt statistische Verteilung mit zeitvariabler Volatilität'}
                        </p>
                      </TextContent>
                    );
                  } catch {
                    return (
                      <TextContent>
                        <p style={{ color: '#d91515' }}>
                          GARCH-Parameter konnten nicht geladen werden.
                        </p>
                      </TextContent>
                    );
                  }
                })()}
              </Box>
            </Box>
          )}
        </SpaceBetween>
      );

    default:
      return null;
  }
};

/**
 * Hilfsfunktionen für Optionsbeschriftungen
 *
 * Diese Funktionen erstellen benutzerfreundliche Beschriftungen für alle
 * Berechnungsstrategien mit integrierten historischen Statistiken.
 */
